import { isMobileAtom } from '@/jotai/atoms/userAtom';
import { Button, Flex, Image, message, Space, Tag } from 'antd';
import { useAtomValue } from 'jotai';

import './index.css';
import RejectDialog from './RejectDialog';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  artworkCertifactionApprove,
  artworkCertificationReject,
  ArtworkVerifyStatus,
  getArtworkVerifyDetail,
} from '@/api/artworkVerify';
import { useQuery } from '@tanstack/react-query';

interface VerifyItemProps {
  id: string;
  actionCallback: () => void;
}

const VerifyItem = ({ id, actionCallback }: VerifyItemProps) => {
  const navigate = useNavigate();
  const [messageApi, contextHolder] = message.useMessage();
  const isMobile = useAtomValue(isMobileAtom);

  const [rejectDialogOpen, setRejectDialogOpen] = useState<boolean>(false);
  const [actionLoading, setActionLoading] = useState<boolean>(false);

  const labelStyle: React.CSSProperties = {
    fontWeight: 'bold',
    padding: isMobile ? 0 : '0 10px',
    width: 300,
    flexShrink: 0,
  };

  const { data: currentArtworkVerify, refetch: refetchCurrentArtworkVerify } =
    useQuery({
      queryKey: ['currentArtworkVerify', id],
      queryFn: async () => {
        const resp = await getArtworkVerifyDetail(id);
        return resp.data;
      },
      enabled: !!id,
    });

  const handleReject = () => {
    setRejectDialogOpen(true);
  };

  const handleApprove = async () => {
    setActionLoading(true);
    const resp = await artworkCertifactionApprove([id]);
    setActionLoading(false);
    if (resp.status !== 200) {
      messageApi.error('认证通过失败');
      return;
    }
    messageApi.success('认证通过');
    await refetchCurrentArtworkVerify();
    void actionCallback();
  };

  const handleRejectConfirm = async (
    selectedKeys: number[],
    remark: string
  ) => {
    setActionLoading(true);
    const resp = await artworkCertificationReject({
      campusCertificationIds: [id],
      dictKeys: selectedKeys.join(','),
      reviewRemark: remark,
    });
    setActionLoading(false);
    if (resp.status !== 200) {
      messageApi.error('认证驳回失败');
      return;
    }
    messageApi.success('认证驳回成功');
    setRejectDialogOpen(false);
    await refetchCurrentArtworkVerify();
    void actionCallback();
  };

  const approveDisabled = useMemo(
    () =>
      !currentArtworkVerify ||
      currentArtworkVerify?.reviewKey !== ArtworkVerifyStatus.PENDING ||
      actionLoading,
    [currentArtworkVerify?.reviewKey, actionLoading]
  );
  const actionDisabled = useMemo(
    () =>
      !currentArtworkVerify ||
      currentArtworkVerify?.reviewKey === ArtworkVerifyStatus.REJECTED ||
      actionLoading,
    [currentArtworkVerify?.reviewKey, actionLoading]
  );

  const renderReviewStatus = useMemo(() => {
    if (currentArtworkVerify?.reviewKey === ArtworkVerifyStatus.APPROVED) {
      return <Tag color="success">认证通过</Tag>;
    }
    if (currentArtworkVerify?.reviewKey === ArtworkVerifyStatus.REJECTED) {
      return <Tag color="error">认证驳回</Tag>;
    }
    return <Tag>待认证</Tag>;
  }, [currentArtworkVerify?.reviewKey]);

  return (
    <div>
      {contextHolder}
      <div>
        <Flex
          style={{
            marginBottom: 10,
            flexDirection: isMobile ? 'column' : 'row',
          }}
        >
          <div style={labelStyle}>认证ID</div>
          <div>{currentArtworkVerify?.campusCertificationId}</div>
        </Flex>
        <Flex
          style={{
            marginBottom: 10,
            flexDirection: isMobile ? 'column' : 'row',
          }}
        >
          <div style={labelStyle}>最近认证状态</div>
          <div>{renderReviewStatus}</div>
        </Flex>
        <Flex
          style={{
            marginBottom: 10,
            flexDirection: isMobile ? 'column' : 'row',
          }}
        >
          <div style={labelStyle}>最近认证时间</div>
          <div>{currentArtworkVerify?.reviewTime}</div>
        </Flex>
        <Flex
          style={{
            marginBottom: 10,
            flexDirection: isMobile ? 'column' : 'row',
          }}
        >
          <div style={labelStyle}>用户真实姓名</div>
          <div>{currentArtworkVerify?.fullName}</div>
        </Flex>
        <Flex
          style={{
            marginBottom: 10,
            flexDirection: isMobile ? 'column' : 'row',
          }}
        >
          <div style={labelStyle}>SUPP ID</div>
          <div>{currentArtworkVerify?.suppId}</div>
        </Flex>
        <Flex
          style={{
            marginBottom: 10,
            flexDirection: isMobile ? 'column' : 'row',
          }}
        >
          <div style={labelStyle}>学校名称</div>
          <div>{currentArtworkVerify?.campusName}</div>
        </Flex>
        <Flex
          style={{
            marginBottom: 10,
            flexDirection: isMobile ? 'column' : 'row',
          }}
        >
          <div style={labelStyle}>学校所在地区</div>
          <div>-</div>
        </Flex>
        <Flex
          style={{
            marginBottom: 10,
            flexDirection: isMobile ? 'column' : 'row',
          }}
        >
          <div style={labelStyle}>用户身份</div>
          <div>-</div>
        </Flex>
        <Flex
          style={{
            marginBottom: 10,
            flexDirection: isMobile ? 'column' : 'row',
          }}
        >
          <Image
            src={currentArtworkVerify?.idCardImageUrl}
            width={isMobile ? 200 : 300}
          />
        </Flex>
      </div>
      <div style={{ textAlign: 'center', marginTop: 20 }}>
        <Space>
          <Button
            type="primary"
            onClick={handleApprove}
            disabled={approveDisabled}
          >
            认证通过
          </Button>
          <Button
            type="primary"
            danger
            onClick={handleReject}
            disabled={actionDisabled}
          >
            认证驳回
          </Button>
          <Button
            onClick={() => navigate('/artwork-verify')}
            disabled={actionLoading}
          >
            取消
          </Button>
        </Space>
      </div>

      <RejectDialog
        isOpen={rejectDialogOpen}
        onClose={() => setRejectDialogOpen(false)}
        onConfirm={(selectedKeys, remark) => {
          void handleRejectConfirm(selectedKeys, remark);
        }}
      />
    </div>
  );
};

const VerifyDetail = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [currentId, setCurrentId] = useState<string>('');
  const idsRef = useRef<string[]>([]);

  useEffect(() => {
    const index = parseInt(searchParams.get('index') || '0');
    const ids = searchParams.get('ids')?.split(',') || [];
    idsRef.current = ids;
    if (ids.length) {
      setCurrentIndex(index);
      setCurrentId(ids[index]);
    }
  }, [searchParams]);

  const handlePrev = useCallback(() => {
    if (currentIndex === 0) {
      return;
    }
    const newIndex = currentIndex - 1;
    setCurrentIndex(newIndex);
    const params = new URLSearchParams(searchParams);
    params.set('index', newIndex.toString());
    setSearchParams(params);
    const prevId = idsRef.current[newIndex];
    if (prevId) {
      setCurrentId(prevId);
    }
  }, [currentIndex, searchParams, setSearchParams]);

  const handleNext = useCallback(() => {
    if (currentIndex === idsRef.current.length - 1) {
      return;
    }
    const newIndex = currentIndex + 1;
    setCurrentIndex(newIndex);
    const params = new URLSearchParams(searchParams);
    params.set('index', newIndex.toString());
    setSearchParams(params);
    const nextId = idsRef.current[newIndex];
    if (nextId) {
      setCurrentId(nextId);
    }
  }, [currentIndex, searchParams, setSearchParams]);

  return (
    <div className="artwork-verify-detail-container">
      <div className="pagination-btn-container">
        <Button
          type="primary"
          shape="circle"
          onClick={handlePrev}
          disabled={currentIndex === 0}
          className="pagination-btn-left"
        >
          &lt;
        </Button>
      </div>

      <div className="artwork-item-container">
        <VerifyItem id={currentId} actionCallback={handleNext} />
      </div>

      <div className="pagination-btn-container">
        <Button
          type="primary"
          shape="circle"
          onClick={handleNext}
          disabled={currentIndex === idsRef.current.length - 1}
          className="pagination-btn-right"
        >
          &gt;
        </Button>
      </div>
    </div>
  );
};

export default VerifyDetail;
